import { useMemo } from "react";
import {
  LayoutGrid, Image as ImageIcon, CalendarDays, Wallet, ShieldCheck, Lightbulb,
  LifeBuoy, ChevronRight, ListChecks, Receipt, MessageCircleQuestion, Gift, Sparkles,
} from "lucide-react";

import { useResource } from "../lib/useApi";
import { money, date, relDays, daysFromToday } from "../lib/fmt";
import { Glass, Section, Skeleton, Err } from "../ui";
import { Offers, QuickTiles } from "./Offers";

/* The rail under the app bar. Reference screen 3 uses it for product
   categories; here it is the six things a client actually opens. */
const RAIL = [
  ["home", "All", LayoutGrid],
  ["ideas", "Ideas", Lightbulb],
  ["designs", "Designs", ImageIcon],
  ["project", "Schedule", CalendarDays],
  ["payments", "Payments", Wallet],
  ["care", "Care", ShieldCheck],
  // Help stays in the rail even though it is no longer a tab. It left the tab
  // bar to make room for Veeha; leaving it ONLY in the drawer would put the
  // one screen a frustrated client goes looking for two taps deep behind a
  // hamburger.
  ["support", "Help", LifeBuoy],
];

export function Home({ ctx, unread }) {
  const { pid, project, open } = ctx;

  const pay = useResource(pid ? `/portal/payments?project_id=${pid}` : null);
  const detail = useResource(pid ? `/portal/project?project_id=${pid}` : null);
  const care = useResource("/portal/care");

  const dues = useMemo(() => summariseDues(pay.data), [pay.data]);

  return (
    <>
      <nav className="rail" aria-label="Quick sections">
        {RAIL.map(([key, label, Icon]) => (
          <button
            key={key}
            className={"rail-item" + (key === "home" ? " on" : "")}
            onClick={() => open(key === "home" ? null : key)}
          >
            <Icon size={19} strokeWidth={1.6} />
            {label}
            {key === "home" && unread > 0 && <span className="rail-badge">{unread}</span>}
          </button>
        ))}
      </nav>

      <Hero dues={dues} loading={pay.loading} detail={detail.data} project={project} open={open} />

      <Err onDismiss={pay.reload}>{pay.error}</Err>

      {/* ---- "My services" row --------------------------------------------- */}
      <Section>
        <ActionsRow detail={detail.data} care={care.data} dues={dues} open={open} />
      </Section>

      {/* ---- next up ------------------------------------------------------- */}
      <NextUp detail={detail.data} loading={detail.loading} open={open} />

      {/* ---- studio add-ons (reference: "Buy Airtel products") -------------- */}
      <Offers slot="tile" title="Add to your home" ctx={ctx} />

      {/* ---- curated (reference: "Curated for you") ------------------------- */}
      <Offers slot="curated" title="Curated for you" ctx={ctx} />

      {/* ---- quick tiles (reference: Call manager / Rewards / Refer & earn) - */}
      <QuickTiles ctx={ctx} />

      {/* ---- you might like ------------------------------------------------ */}
      <Offers slot="might_like" title="You might like" ctx={ctx} />

      <p className="hand" style={{ textAlign: "center", padding: "6px 24px 20px" }}>
        Everything here is yours — ask Veeha if anything is unclear.
      </p>
    </>
  );
}

/* =========================================================================
   HERO
   Two states, chosen by whether money is owed. Reference screen 3 puts the
   bill at the very top and everything else below it; a client with a balance
   is looking for exactly that, and a client with none should not be shown a
   payment prompt at all.
   ========================================================================= */

function Hero({ dues, loading, detail, project, open }) {
  if (loading) {
    return (
      <div className="hero" style={{ padding: 0 }}>
        <Skeleton h={190} r={24} />
      </div>
    );
  }

  const owes = dues.balance > 0;
  const progress = Number(detail?.progress ?? project?.progress ?? 0);

  if (owes) {
    const late = dues.dueDate && daysFromToday(dues.dueDate) < 0;
    return (
      <Glass className="hero" strong>
        <span className="hero-eyebrow">{late ? "Payment overdue" : "Payment status"}</span>
        <div className="hero-top">
          <span className="hero-tag"><Receipt size={14} /> {dues.count} open invoice{dues.count === 1 ? "" : "s"}</span>
          <span className="hero-code">{project?.project_code}</span>
        </div>
        <p className="hero-amt">{money(dues.balance)}</p>
        <p className="hero-sub">
          {dues.dueDate ? (
            <>Due <b>{date(dues.dueDate)}</b> — {relDays(dues.dueDate)}. Paying on schedule keeps your
            installation dates intact.</>
          ) : (
            <>Outstanding against your approved schedule of payments.</>
          )}
        </p>
        <button className="hero-cta" onClick={() => open("payments")}>Pay now</button>
        <div className="hero-links">
          <button onClick={() => open("payments")}>View invoices</button>
          <button onClick={() => open("project")}>What it covers</button>
        </div>
      </Glass>
    );
  }

  return (
    <Glass className="hero" strong>
      <span className="hero-eyebrow">Your project</span>
      <div className="hero-top">
        <span className="hero-tag"><ListChecks size={14} /> {detail?.task_done ?? 0} of {detail?.task_total ?? 0} tasks done</span>
        <span className="hero-code">{project?.project_code}</span>
      </div>
      <p className="hero-amt" style={{ fontSize: "2.4rem" }}>{progress}%</p>
      <div className="hero-track"><i style={{ width: Math.min(100, Math.max(2, progress)) + "%" }} /></div>
      <p className="hero-sub">
        {dues.count > 0
          ? <>All invoices are settled — thank you. <b>Nothing is due.</b></>
          : <>No payment is due right now. We will tell you before the next stage falls due.</>}
      </p>
      <button className="hero-cta calm" onClick={() => open("project")}>See what&rsquo;s happening</button>
      <div className="hero-links">
        <button onClick={() => open("designs")}>Designs</button>
        <button onClick={() => open("payments")}>Payment history</button>
      </div>
    </Glass>
  );
}

/* =========================================================================
   ACTIONS ROW — reference screen 3's "4 Services · 4 actions due"
   ========================================================================= */

function ActionsRow({ detail, care, dues, open }) {
  const actions = [];
  if (dues.balance > 0) actions.push("a payment");
  if (care?.steps?.review && !care.steps.review.done && care.steps.payment?.done) actions.push("your review");
  if (detail && detail.task_total > detail.task_done) {
    const left = detail.task_total - detail.task_done;
    actions.push(`${left} task${left === 1 ? "" : "s"} in flight`);
  }

  const milestonesLeft = (detail?.milestones || []).filter((m) => !m.completed).length;

  return (
    <button className="svc glass" onClick={() => open("project")}>
      <span className="svc-ic"><ListChecks size={19} /></span>
      <span className="svc-txt">
        <b>{milestonesLeft > 0 ? `${milestonesLeft} milestones ahead` : "Your project"}</b>
        {actions.length ? (
          <span>Waiting on {actions.join(" · ")}</span>
        ) : (
          <span className="calm">Everything is on track</span>
        )}
      </span>
      <ChevronRight size={18} color="var(--ink-soft)" />
    </button>
  );
}

/* =========================================================================
   NEXT UP — the next two open milestones, so Home answers "what happens next"
   without a tap.
   ========================================================================= */

function NextUp({ detail, loading, open }) {
  const next = (detail?.milestones || []).filter((m) => !m.completed).slice(0, 2);
  if (loading) {
    return (
      <Section title="Next up">
        <div className="wrap"><Skeleton h={64} /></div>
      </Section>
    );
  }
  if (!next.length) return null;

  return (
    <Section title="Next up" more="Full schedule" onMore={() => open("project")}>
      <div className="wrap">
        {next.map((m) => {
          const pct = m.task_total > 0 ? Math.round((m.task_done / m.task_total) * 100) : 0;
          return (
            <div className="row glass" key={m.id}>
              <span className="row-ic"><CalendarDays size={17} /></span>
              <span className="row-txt">
                <b>{m.title}</b>
                <small>{m.task_done} of {m.task_total} tasks · {pct}%</small>
              </span>
              <span className="row-end">
                <b style={{ fontSize: ".82rem" }}>{date(m.due_date)}</b>
                <small>{relDays(m.due_date)}</small>
              </span>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/* ---- helpers ------------------------------------------------------------- */

function summariseDues(payments) {
  const list = Array.isArray(payments) ? payments : [];
  let balance = 0;
  let dueDate = null;
  let open = 0;
  for (const inv of list) {
    const b = Number(inv?.summary?.balance || 0);
    if (b > 0.5) {
      balance += b;
      open += 1;
      if (inv.due_date && (!dueDate || inv.due_date < dueDate)) dueDate = inv.due_date;
    }
  }
  return { balance: Math.round(balance), dueDate, count: open || list.length };
}

export { MessageCircleQuestion, Gift, Sparkles, ImageIcon };
